import { Badge, Center, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';

import { MODULE_DISPLAY_INFO } from 'app/components/Companies/CreateCompany/CreateCompanyForm/types';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';

interface ModulesOverviewProps {
  companyId?: string;
}

type ModuleState = 'ENABLED' | 'DISABLED' | 'UNAVAILABLE';

const STATE_META: Record<ModuleState, { label: string; colorScheme: string }> = {
  ENABLED: { label: 'Enabled', colorScheme: 'green' },
  DISABLED: { label: 'Disabled', colorScheme: 'orange' },
  UNAVAILABLE: { label: 'Unavailable', colorScheme: 'gray' },
};

// A module the company hasn't been provisioned for is absent from modulesConfig → "Unavailable".
const getModuleState = (value?: string): ModuleState => {
  if (value === 'ENABLED') {
    return 'ENABLED';
  }
  if (value === 'DISABLED') {
    return 'DISABLED';
  }
  return 'UNAVAILABLE';
};

function ModulesOverview({ companyId = undefined }: ModulesOverviewProps) {
  const { data: configuration, isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const modules = configuration?.modulesConfig?.modules ?? {};

  return (
    <Flex flexDir="column" gap="16px" mt="30px">
      <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
        Modules
      </Text>

      {isInitialLoading ? (
        <Center py="40px">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="12px">
          {Object.entries(MODULE_DISPLAY_INFO).map(([key, info]) => {
            const meta = STATE_META[getModuleState(modules[key])];

            return (
              <Flex
                key={key}
                justify="space-between"
                align="center"
                gap="12px"
                border="1px solid"
                borderColor="brand.lightGray1"
                borderRadius="8px"
                px="16px"
                py="12px"
              >
                <Text textStyle="smallMedium" color="brand.darkPurple">
                  {info.title}
                </Text>
                <Badge colorScheme={meta.colorScheme} variant="subtle" flexShrink={0}>
                  {meta.label}
                </Badge>
              </Flex>
            );
          })}
        </SimpleGrid>
      )}
    </Flex>
  );
}

export { ModulesOverview };

export default ModulesOverview;
